class ProductInfo extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this._galleryAbortCtrl = null;
    this._galleryObserver = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    const dataEl = this.querySelector('script[data-product-variants]');
    if (dataEl) {
      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('Error parsing product variants', e);
      }
    }

    this.onChangeHandler = this.onChange.bind(this);
    document.addEventListener('change', this.onChangeHandler);

    this.initSelectedVariant();
    this.initGalleryObserver();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onChangeHandler);
    if (this._galleryObserver) this._galleryObserver.disconnect();
  }

  // Watch gallery modal for is-active — apply deferred swiper reset on open
  initGalleryObserver() {
    const modalEl = document.querySelector('[data-modal="gallery"]');
    if (!modalEl) return;

    this._galleryObserver = new MutationObserver(() => {
      if (!modalEl.classList.contains('is-active')) return;
      const swiperEl = modalEl.querySelector('[data-slider="gallery"]');
      if (!swiperEl || !swiperEl._galleryNeedsReset) return;

      swiperEl._galleryNeedsReset = false;
      const swiper = swiperEl.swiper;
      if (swiper) {
        swiper.update();
        swiper.slideTo(0, 0);
      }
    });

    this._galleryObserver.observe(modalEl, { attributes: true, attributeFilter: ['class'] });
  }

  variantByOption(value) {
    return this.variants.find(v => v.option === value) || null;
  }

  setImage(url) {
    if (!url) return;
    const wrapper = this.querySelector('[data-variant-image-wrapper]');
    if (!wrapper) return;

    let img = wrapper.querySelector('img');
    if (img) {
      img.src = url;
    } else {
      img = document.createElement('img');
      img.width = 48;
      img.height = 80;
      img.loading = 'eager';
      wrapper.appendChild(img);
      img.src = url;
    }
  }

  onChange(e) {
    if (!e.target) return;

    if (e.target.name === 'product-variant') {
      const variant = this.variantByOption(e.target.value);
      if (variant) {
        if (variant.image) this.setImage(variant.image);
        this.updateGallery(variant.id);
      }
      return;
    }

    // Sticky panel variant selector
    if (e.target.name === 'id' && e.target.closest('product-sticky-panel')) {
      this.updateGallery(parseInt(e.target.value, 10));
    }
  }

  initSelectedVariant() {
    const checked = document.querySelector('[name="product-variant"]:checked');
    if (checked) {
      const variant = this.variantByOption(checked.value);
      if (variant && variant.image) this.setImage(variant.image);
    }
  }

  async updateGallery(variantId) {
    if (!variantId) return;
    const handle = this.dataset.productHandle;
    const sectionId = this.dataset.section;
    if (!handle || !sectionId) return;

    if (this._galleryAbortCtrl) this._galleryAbortCtrl.abort();
    this._galleryAbortCtrl = new AbortController();

    try {
      const res = await fetch(
        `/products/${handle}?sections=${sectionId}&variant=${variantId}`,
        { signal: this._galleryAbortCtrl.signal }
      );
      if (!res.ok) return;

      const data = await res.json();
      const html = data[sectionId];
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Replace trigger visibility (Liquid re-evaluates conditions for new variant)
      const newTrigger = doc.querySelector('[data-gallery-trigger]');
      const oldTrigger = document.querySelector('[data-gallery-trigger]');
      if (oldTrigger && newTrigger) {
        oldTrigger.replaceWith(newTrigger.cloneNode(true));
      }

      // Swap only slide markup inside the live swiper-wrapper, keep Swiper element alive
      const newSlidesWrapper = doc.querySelector('[data-gallery-slides]');
      const oldSlidesWrapper = document.querySelector('[data-gallery-slides]');

      if (oldSlidesWrapper && newSlidesWrapper) {
        const swiperEl = oldSlidesWrapper.closest('[data-slider="gallery"]');
        const swiper = swiperEl && swiperEl.swiper;

        oldSlidesWrapper.innerHTML = newSlidesWrapper.innerHTML;

        if (swiper) {
          const modalEl = document.querySelector('[data-modal="gallery"]');
          const isOpen = modalEl && modalEl.classList.contains('is-active');

          if (isOpen) {
            swiper.update();
            swiper.slideTo(0, 0);
          } else {
            swiperEl._galleryNeedsReset = true;
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Gallery update failed', err);
    }
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}
